import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/leuteliaApi";
import { Button } from "@/components/ui/button";
import { GitCompare, ArrowRight, ArrowUpRight, ArrowDownRight, Minus, Sparkles, Loader2, RefreshCw } from "lucide-react";

const LABEL = {
  criticita: "Problemi", contraddizioni: "Da verificare", segnalazioni: "Segnalazioni", opportunita: "Opportunità", punti_di_forza: "Punti di forza",
  sicurezza_identita: "Sicurezza identità %", voto_google: "Voto Google", recensioni_google: "Recensioni Google", foto: "Foto", copertura: "Quadro",
};
const GOOD_UP = new Set(["opportunita", "punti_di_forza", "sicurezza_identita", "voto_google", "recensioni_google", "foto"]);
const COV = { HEALTHY: "completo", DEGRADED_BUT_USABLE: "parziale affidabile", PARTIAL_BUT_VALID: "parziale", BLOCKED_TECHNICALLY: "insufficiente" };
const TONE = { positivo: "border-emerald-300 bg-emerald-50/60", neutro: "border-brand-gold/30 bg-brand-cream/60", attenzione: "border-orange-300 bg-orange-50/60" };
const fmt = (k, v) => (v == null ? "—" : k === "copertura" ? COV[v] || v : String(v));
const when = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export default function Confronto({ ccu, scope }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { try { setData(await api.changes(scope.tenant, scope.hotel)); } catch (e) { toast.error(e.message); } };
  useEffect(() => { load(); }, [scope.tenant, scope.hotel, ccu.understanding_version]); // eslint-disable-line react-hooks/exhaustive-deps
  const read = async () => {
    setBusy(true);
    try { const r = await api.changesReading({ tenant: scope.tenant, hotel: scope.hotel }); setData((d) => ({ ...d, reading: r })); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  useEffect(() => { if (data && data.from_version != null && !data.reading && !busy) read(); }, [data?.to_version, data?.from_version]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="bg-white rounded-xl border border-brand-gold/25 p-5 text-[13px] text-brand-muted flex items-center gap-2"><Loader2 size={14} className="animate-spin text-brand-gold" /> confronto…</div>;
  const d = data.delta;
  const first = data.from_version == null;
  const lists = [
    ["#B0452E", "Nuovi problemi", d.new_criticalities.map((c) => c.text)],
    ["#2F6B4F", "Problemi risolti", d.resolved_criticalities.map((c) => c.text)],
    ["#C2662E", "Nuove cose da verificare", d.new_contradictions.map((c) => c.field.replace(/_/g, " "))],
    ["#2F6B4F", "Verifiche chiuse", d.resolved_contradictions.map((c) => c.field.replace(/_/g, " "))],
    ["#C6902A", "Identità che si sono spostate", d.identity_shifts.map((s) => `${s.dimension}: ${s.previous || "—"} → ${s.current}`)],
  ].filter((l) => l[2].length);

  return (
    <div className="bg-white rounded-xl border border-brand-gold/25 p-5" data-testid="confronto-panel">
      <div className="flex items-start gap-2.5">
        <GitCompare size={20} className="text-brand-gold mt-0.5" strokeWidth={1.9} />
        <div className="flex-1">
          <h3 className="font-serif text-[20px] text-brand-ink leading-none">Cosa è cambiato dall'ultima scansione</h3>
          <p className="text-[12.5px] text-brand-muted mt-1" data-testid="confronto-sub">
            {first ? "Prima lettura del tuo hotel: il confronto apparirà dalla prossima scansione." : <>Lettura <strong>v{data.from_version}</strong> del {when(data.from_at)} <ArrowRight size={11} className="inline mx-0.5" /> <strong>v{data.to_version}</strong> del {when(data.to_at)}</>}
          </p>
        </div>
      </div>

      {!first && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2" data-testid="confronto-metrics">
          {Object.keys(LABEL).filter((k) => data.now[k] != null || data.before?.[k] != null).map((k) => {
            const b = data.before?.[k], a = data.now[k];
            const changed = b !== a && b != null;
            const up = typeof a === "number" && typeof b === "number" ? a > b : null;
            const good = up == null ? null : GOOD_UP.has(k) ? up : !up;
            const col = !changed ? "#9C968A" : good ? "#2F6B4F" : "#B0452E";
            const Icon = !changed ? Minus : up ? ArrowUpRight : ArrowDownRight;
            return (
              <div key={k} className={`rounded-lg border px-3 py-2 ${changed ? "border-brand-gold/40 bg-brand-cream/60" : "border-brand-gold/15"}`} data-testid={`confronto-metric-${k}`}>
                <div className="text-[10.5px] text-brand-muted uppercase tracking-wide">{LABEL[k]}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {changed && <span className="text-[12px] text-brand-muted line-through">{fmt(k, b)}</span>}
                  <span className="font-serif text-[18px] leading-none text-brand-ink">{fmt(k, a)}</span>
                  <Icon size={14} style={{ color: col }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {lists.length > 0 && (
        <div className="mt-4 divide-y divide-brand-gold/15">
          {lists.map(([tone, label, items]) => (
            <div key={label} className="py-2">
              <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: tone }}>{label}</div>
              {items.map((t, i) => <div key={i} className="text-[13px] text-brand-ink">• {t}</div>)}
            </div>
          ))}
        </div>
      )}
      {!first && !lists.length && !data.changes.length && <p className="mt-3 text-[13px] text-brand-muted italic">Numeri e segnalazioni invariati rispetto alla lettura precedente.</p>}

      {!first && (
        <div className={`mt-4 rounded-xl border p-4 ${TONE[data.reading?.tone] || TONE.neutro}`} data-testid="confronto-reading">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-orange"><Sparkles size={13} /> La lettura di LEUTELIA</div>
            {data.reading && <button onClick={read} disabled={busy} className="text-[11.5px] text-brand-muted hover:text-brand-green flex items-center gap-1" data-testid="confronto-reading-refresh"><RefreshCw size={11} className={busy ? "animate-spin" : ""} /> aggiorna</button>}
          </div>
          {data.reading ? (
            <>
              <div className="font-serif text-[18px] text-brand-ink mt-2 leading-tight" data-testid="confronto-reading-headline">{data.reading.headline}</div>
              <p className="text-[13.5px] text-brand-ink mt-2 leading-relaxed">{data.reading.reading}</p>
              {data.reading.next_step && <p className="text-[13px] text-brand-green font-semibold mt-2 flex items-start gap-1.5"><ArrowRight size={14} className="mt-0.5 shrink-0" /> {data.reading.next_step}</p>}
            </>
          ) : busy ? (
            <div className="flex items-center gap-2 text-[13px] text-brand-muted mt-2"><Loader2 size={14} className="animate-spin text-brand-gold" /> LEUTELIA sta leggendo i cambiamenti…</div>
          ) : (
            <Button onClick={read} variant="outline" className="mt-2 h-9 border-brand-green/30 text-brand-green text-[12.5px] font-semibold" data-testid="confronto-reading-generate"><Sparkles size={14} className="mr-2" /> Leggi i cambiamenti</Button>
          )}
        </div>
      )}
    </div>
  );
}
